// Persistência do guia (drawer) da Jornada de Configuração em localStorage.
// O drawer sobrevive à troca de rota; ao recarregar a página, o estado do guia
// (aberto + tarefa) é restaurado a partir daqui. Segue o mesmo padrão defensivo
// de `@modules/design/utils/welcome-storage.js` (try/catch — storage corrompido
// é cenário esperado e tratado como ausência de dados).
//
// A chave é carimbada por escritório + usuário (mesmo padrão de
// `@modules/atendimento/utils/conexao-filter-storage`): o guia aponta para uma
// etapa de um catálogo que é curado POR ESCRITÓRIO, então uma chave global
// vazava o estado entre contas/logins na mesma máquina — o guia voltava aberto
// apontando para um `taskId` que não existe no catálogo do escritório novo,
// e o painel abria vazio deslocando o layout em 380px.

import { normalizeGuideOrigin } from '@modules/central-config/utils/guide-origin'

const GUIDE_STORAGE_PREFIX = 'cc_guide'

// `escritorioId`/`userId` ausentes viram 'na' em vez de gerar chave sem escopo:
// uma chave parcial é melhor que uma chave compartilhada por todo mundo.
export const guideStorageKey = ({ escritorioId, userId } = {}) =>
  `${GUIDE_STORAGE_PREFIX}_${escritorioId || 'na'}_${userId || 'na'}`

// Escopo de persistência a partir do store (escritório + usuário logados).
// Fica aqui, e não em cada consumidor, para que a chave seja montada de um
// único jeito — divergir o escopo entre leitura e escrita perde o estado.
export const guideScopeFromStore = (store) => {
  // Store/getters ausentes caem no escopo 'na' em vez de lançar: persistir o
  // guia é best-effort e nunca pode derrubar a action que o abre/fecha.
  const getters = (store && store.getters) || {}
  const user = getters['user/currentUser'] || {}
  const escritorio = user.escritorio || {}
  return { escritorioId: escritorio.id_interno, userId: user.id_interno }
}

// Parse defensivo: localStorage adulterado/corrompido não pode quebrar o módulo.
const tryJsonParse = (raw) => {
  try {
    return JSON.parse(raw)
  } catch (error) { // NOSONAR: storage corrompido é cenário esperado, tratado como ausência
    return null
  }
}

// Lê o guia persistido. Retorna null quando ausente/inválido (nunca undefined).
export const readGuide = (scope) => {
  try {
    const raw = localStorage.getItem(guideStorageKey(scope))
    if (!raw) return null
    const parsed = tryJsonParse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return {
      open: !!parsed.open,
      collapsed: !!parsed.collapsed,
      moduleId: parsed.moduleId || null,
      taskId: parsed.taskId || null,
      // Payload gravado antes da origem existir não tem `origin`:
      // normalizeGuideOrigin o devolve como 'journey'. É a migração inteira —
      // sem versionar chave nem limpar o storage de quem já tinha guia aberto.
      origin: normalizeGuideOrigin(parsed.origin),
      moduleKey: parsed.moduleKey || null,
    }
  } catch (error) { // NOSONAR: acesso a localStorage pode falhar (modo privado); degrada para null
    return null
  }
}

// Grava o guia. Falha de escrita (quota/modo privado) é silenciada — o estado
// em memória (Vuex) continua válido mesmo sem persistência.
export const writeGuide = (guide, scope) => {
  try {
    // ⚠️ `origin`/`moduleKey` PRECISAM estar aqui. Sem eles o F5 restaura o guia
    // sem saber de onde ele foi aberto, e o botão de voltar passa a levar à home
    // da Jornada mesmo tendo sido aberto de dentro de um módulo — falha
    // silenciosa, sem erro em tela nem no console.
    const payload = {
      open: !!(guide && guide.open),
      collapsed: !!(guide && guide.collapsed),
      moduleId: (guide && guide.moduleId) || null,
      taskId: (guide && guide.taskId) || null,
      origin: normalizeGuideOrigin(guide && guide.origin),
      moduleKey: (guide && guide.moduleKey) || null,
    }
    localStorage.setItem(guideStorageKey(scope), JSON.stringify(payload))
  } catch (error) { // NOSONAR: persistência é best-effort; ausência dela não quebra o guia
    // no-op
  }
}
